// The app's own user-facing release notes (docs/release-notes.json, shared by all clients). The reference
// prototype's changelog is shown only in the in-memory demo workspace used for visual parity.
// An item may carry a screenshot (v6 F2): `img` is a path under the app's public base (bundled static asset,
// brand-neutral), `ratio` an optional CSS aspect ratio (default 26 / 33, the reference value).
// Design v7: every item has an area (`areas` in the JSON) and an optional rank; a release may list highlights.
// The Changelog pane shows the notes as a list or grouped by area (each version starting with its highlights),
// in English or Persian (reference L1659–1676).
import { ref } from 'vue';
import notes from '../../../../docs/release-notes.json';
import { api } from '@/api/client';
import type { ChangelogItem } from './constants';
import { REFERENCE_CHANGELOG, REFERENCE_DOC_FA, REFERENCE_GROUPS, REFERENCE_HIGHLIGHTS, REFERENCE_META, type DocFa } from './referenceChangelog';
import { t } from '@/app/i18n';

export interface ChangelogLine { text: string; img?: string; ratio?: string; area?: string; rank?: number }
export type ChangelogEntry = { v: string; date: string; current: boolean; items: ChangelogLine[] };
type NoteItem = { en: string; fa: string; area: string; rank?: number; img?: string; ratio?: string; phases: string[] };
type Highlight = { en: string; fa: string };
type Release = { version: string; status: string; date: string | null; highlights?: Highlight[]; items: NoteItem[]; /** v10: written Persian description shown instead of the grouped list */ doc_fa?: DocFa };

export const APP_VERSION: string = notes.current;
const RELEASES = notes.releases as Release[];
/** The current version has not been released yet (no release date). */
// (`current` may have no entry: admin/infrastructure-only versions are never listed — owner rule 2026-10-16)
export const PRE_RELEASE: boolean = (RELEASES.find(r => r.version === notes.current)?.status ?? 'released') !== 'released';
/** Areas of the grouped view, in display order: [key, English, Persian] (reference CL_GROUPS). */
export const AREAS: Array<[string, string, string]> = notes.areas.map(a => [a.key, a.en, a.fa]);

/** Versions a brand admin hid from What's new (admin console → تغییرات نسخه; GET /api/v1/release-notes). */
const hiddenVersions = ref<string[]>([]);
let hiddenFetched = false;
/** Loads the hidden versions once per page load (online only; offline keeps every bundled release visible). */
export function refreshHiddenReleases(): void {
  if (hiddenFetched) return;
  hiddenFetched = true;
  api<{ hidden_builtin?: string[] }>('GET', '/release-notes')
    .then(r => { hiddenVersions.value = Array.isArray(r.hidden_builtin) ? r.hidden_builtin : []; })
    .catch(() => { hiddenFetched = false; });
}
const visibleReleases = (): Release[] => RELEASES.filter(r => !hiddenVersions.value.includes(r.version));

const line = (text: string, it: { img?: string; ratio?: string }): ChangelogLine => ({ text, ...(it.img ? { img: it.img } : {}), ...(it.ratio ? { ratio: it.ratio } : {}) });

/** Release date for the pane: "Oct 6, 2026" (Persian calendar in Persian); undated = still in development. */
function dateLabel(iso: string | null, fa: boolean): string {
  if (!iso) return fa ? 'در حال توسعه' : t('In development');
  const d = new Date(iso + 'T12:00:00Z');
  return fa ? d.toLocaleDateString('fa-IR', { calendar: 'persian', month: 'short', day: 'numeric', year: 'numeric' }) : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function appChangelog(lang: string): ChangelogEntry[] {
  const fa = lang === 'fa';
  return visibleReleases().map(r => ({
    v: r.version,
    date: dateLabel(r.date, fa),
    current: r.version === notes.current,
    items: r.items.map(i => ({ ...line(fa ? i.fa : i.en, i), area: i.area, ...(i.rank !== undefined ? { rank: i.rank } : {}) })),
  }));
}

const refLine = (it: ChangelogItem): ChangelogLine => (typeof it === 'string' ? { text: it } : line(it.text, it));

export const changelogFor = (demo: boolean | undefined, lang: string): ChangelogEntry[] =>
  demo ? REFERENCE_CHANGELOG.map(c => ({ v: c.v, date: c.date, current: !!c.current, items: c.items.map(refLine) })) : appChangelog(lang);

/** Absolute URL of a bundled changelog image (served from the app's own origin and base path). */
export const changelogImageUrl = (img: string): string => (import.meta.env.BASE_URL || '/') + img.replace(/^\/+/, '');

/** View-model of one item for the Changelog pane (reference L1401 mapping). */
export function changelogItemVals(it: ChangelogLine): { text: string; hasImg: boolean; img?: string; imgBg?: string; ratio?: string } {
  if (!it.img) return { text: it.text, hasImg: false };
  const src = changelogImageUrl(it.img);
  return { text: it.text, hasImg: true, img: src, imgBg: 'center / cover no-repeat url("' + src + '")', ratio: it.ratio || '26 / 33' };
}

/** Persian lines end with a full stop like the reference (`/[.!؟?]$/` already ends one). */
const faStop = (s: string): string => (/[.!؟?]$/.test(s.trim()) ? s : s.trim() + '.');

export interface ChangelogGroupVals { label: string; showLabel: boolean; items: Array<ReturnType<typeof changelogItemVals> & { strong?: boolean; fw?: number; fc?: string }> }
type DocVals = { overview: Array<{ parts: DocPartVals[] }>; sections: Array<{ title: string; intro: string; hasIntro: boolean; items: Array<{ parts: DocPartVals[]; hasSubs: boolean; subs: Array<{ parts: DocPartVals[] }> }> }> };
type DocPartVals = { text: string; fw: number; fc: string };
export interface ChangelogVersionVals { v: string; date: string; current: boolean; empty: boolean; groups: ChangelogGroupVals[]; hasDoc: boolean; doc: DocVals }

/** Design v10 CL_DOC_FA view-model: bold parts in the primary text colour. */
function docVals(d: DocFa): DocVals {
  const mapP = (ps: Array<{ text: string; b: boolean }>) => ps.map(p => ({ text: p.text, fw: p.b ? 700 : 400, fc: p.b ? 'var(--ne7e7ea, #e7e7ea)' : 'inherit' }));
  return { overview: d.overview.map(o => ({ parts: mapP(o.parts) })), sections: d.sections.map(sc => ({ title: sc.title, intro: sc.intro || '', hasIntro: !!sc.intro, items: sc.items.map(it => ({ parts: mapP(it.parts), hasSubs: !!(it.subs && it.subs.length), subs: (it.subs || []).map(sb => ({ parts: mapP(sb.parts) })) })) })) };
}

/**
 * Changelog pane view-model (reference L1662–1675): List = one unlabeled group in release order; Grouped = one group
 * per area in AREAS order (items reversed then stably sorted by rank, default 1), preceded by the highlights.
 */
export function changelogPane(demo: boolean | undefined, fa: boolean, grouped: boolean): ChangelogVersionVals[] {
  const src: Array<ChangelogEntry & { highlights?: Highlight[] }> = demo
    ? REFERENCE_CHANGELOG.map(c => {
      const meta = REFERENCE_META[c.v] || [];
      return { v: c.v, date: c.date, current: !!c.current, highlights: (REFERENCE_HIGHLIGHTS[c.v] || []).map(([en, f]) => ({ en, fa: f })),
        items: c.items.map((it, i) => { const m = meta[i] || ['app', ''], en = refLine(it); return { ...en, text: fa && m[1] ? m[1] : en.text, area: m[0], rank: m[2] ?? 1 }; }) };
    })
    : (() => { refreshHiddenReleases(); const app = appChangelog(fa ? 'fa' : 'en'); return app.map(e => ({ ...e, highlights: RELEASES.find(r => r.version === e.v)?.highlights || [] })); })();
  const areas = demo ? REFERENCE_GROUPS : AREAS;
  return src.map(c => {
    // v10: the current version's first item (its latest change) reads bold in the primary text colour; v11: only while
    // that version is still in progress (not yet released).
    const inProgress = demo ? c.date === 'In progress' : !RELEASES.find(r => r.version === c.v)?.date;
    const items = c.items.map((it, i) => { const latest = c.current && inProgress && i === 0;
      return { ...changelogItemVals({ ...it, text: fa ? faStop(it.text) : it.text }), area: it.area || 'app', rank: it.rank ?? 1, fw: latest ? 700 : 400, fc: latest ? 'var(--ne7e7ea, #e7e7ea)' : 'var(--nc9cacf, #c9cacf)' }; });
    const strip = ({ area: _a, rank: _r, ...rest }: typeof items[number]) => rest;
    let groups: ChangelogGroupVals[];
    if (grouped) {
      groups = areas.map(([k, en, f]) => ({ label: fa ? f : en, showLabel: true, items: items.filter(x => x.area === k).reverse().sort((p, q) => p.rank - q.rank).map(strip) })).filter(g => g.items.length);
      if (c.highlights && c.highlights.length) groups.unshift({ label: fa ? 'خلاصه' : 'Highlights', showLabel: true, items: c.highlights.map(h => ({ text: fa ? faStop(h.fa) : h.en, hasImg: false, strong: true })) });
    } else groups = [{ label: '', showLabel: false, items: items.map(strip) }];
    const dd = fa ? (demo ? REFERENCE_DOC_FA[c.v] : RELEASES.find(r => r.version === c.v)?.doc_fa) : undefined;
    return { v: c.v, date: c.date, current: c.current, empty: !c.items.length && !dd, groups: dd ? [] : groups, hasDoc: !!dd, doc: dd ? docVals(dd) : { overview: [], sections: [] } };
  });
}
